import React, { useEffect, useState } from 'react'
import { callCenterApi } from '@/api/callcenter'
import { Button } from '@/components/ui/Button'
import CustomerSelectModal, { SelectCustomerItem } from '@/components/shared/CustomerSelectModal'
import BankSelectModal from '@/components/shared/BankSelectModal'
import TeamSelectModal from '@/components/shared/TeamSelectModal'
import apiClient from '@/api/client'

const AdminInvestments: React.FC = () => {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [teamId, setTeamId] = useState<number | undefined>(undefined)
  const [filters, setFilters] = useState<{ status?: string; search?: string; bank_id?: number; customer_id?: number }>({})

  const [teamModal, setTeamModal] = useState(false)
  const [customerModal, setCustomerModal] = useState(false)
  const [bankModal, setBankModal] = useState(false)
  const [teamName, setTeamName] = useState<string | undefined>(undefined)

  const fmt = (n:number)=> (Number(n||0).toLocaleString('tr-TR'))
  const fmtMoney = (n:number)=> `€ ${fmt(n)}`

  const load = async ()=>{
    setLoading(true)
    try{
      const res = await callCenterApi.investments.list({ page, page_size: pageSize, status: filters.status, search: filters.search, team: teamId, bank_id: filters.bank_id, customer_id: filters.customer_id })
      setItems(res.items||[]); setTotal(res.total||0)
    } finally{ setLoading(false) }
  }

  useEffect(()=>{ load() }, [page, pageSize, filters.status, filters.search, teamId, filters.bank_id, filters.customer_id])

  const openTeamPicker = () => setTeamModal(true)

  const loadCustomers = async (q: string): Promise<SelectCustomerItem[]> => {
    if (!teamId) return []
    try {
      const res = await apiClient.get('/api/v1/users', { params: { role: 'customer', page_size: 100, team_id: teamId, search: q || undefined } })
      const users = res.data?.users || []
      return users.map((u:any)=> ({ id: u.id, name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email, email: u.email }))
    } catch {
      return []
    }
  }

  const inputCls = "px-3 py-2 border border-slate-300 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
  const chipBtnCls = "px-3 py-2 border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3 pb-5 border-b border-slate-200">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Yönetim</p>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Yatırımlar</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button className={chipBtnCls} onClick={openTeamPicker}>{teamName ? `Takım: ${teamName}` : 'Takım Seç'}</button>
          <button className={chipBtnCls} disabled={!teamId} onClick={()=> setCustomerModal(true)}>{filters.customer_id ? `Müşteri: #${filters.customer_id}` : 'Müşteri Seç'}</button>
          <button className={chipBtnCls} onClick={()=> setBankModal(true)}>{filters.bank_id ? `Banka: #${filters.bank_id}` : 'Banka Seç'}</button>
          <input className={inputCls} placeholder="Ara (müşteri/banka)" onChange={e=> setFilters(f=>({...f, search:e.target.value}))} />
          <select className={inputCls} value={filters.status||''} onChange={e=> setFilters(f=>({...f, status: e.target.value || undefined}))}>
            <option value="">Durum (hepsi)</option>
            <option value="active">Aktif</option>
            <option value="matured">Tamamlanan</option>
            <option value="cancelled">İptal</option>
          </select>
          <Button onClick={()=>load()} disabled={loading}>Yenile</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600 p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Takım</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Tarih</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Müşteri</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Banka</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Tutar</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Vade (Ay)</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Faiz</th>
                <th className="py-2 pr-4 text-xs font-semibold uppercase tracking-wide">Durum</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-4 text-center text-slate-500">Yükleniyor...</td></tr>
              ) : items.length===0 ? (
                <tr><td colSpan={8} className="py-4 text-center text-slate-500">Kayıt yok</td></tr>
              ) : items.map((it:any)=> (
                <tr key={it.id} className={`border-b border-slate-100 ${it.status==='cancelled' ? 'bg-danger-50' : 'hover:bg-slate-50'}`}>
                  <td className="py-2 pr-4 text-slate-700">{it.team_id ?? '-'}</td>
                  <td className="py-2 pr-4 text-slate-700">{it.start_date?.slice(0,10) || '-'}</td>
                  <td className="py-2 pr-4 text-slate-900 font-medium">{it.customer_name || `#${it.customer_id}`}</td>
                  <td className="py-2 pr-4 text-slate-700">{it.bank_name || `#${it.bank_id}`}</td>
                  <td className="py-2 pr-4 text-slate-900 tabular-nums">{fmtMoney(it.amount||0)}</td>
                  <td className="py-2 pr-4 text-slate-700 tabular-nums">{it.duration_months ?? '-'}</td>
                  <td className="py-2 pr-4 text-slate-700 tabular-nums">%{it.interest_rate}</td>
                  <td className="py-2 pr-4">{it.status==='cancelled' ? (<span className="inline-block px-2 py-0.5 text-xs font-medium bg-danger-50 text-danger-700 border border-danger-200">İptal oldu</span>) : <span className="text-slate-700">{it.status}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <div className="text-sm text-slate-600">Toplam <span className="tabular-nums font-medium text-slate-900">{total.toLocaleString('tr-TR')}</span></div>
          <div className="flex items-center gap-2">
            <button className={chipBtnCls + " py-1"} disabled={page<=1} onClick={()=> setPage(p=>p-1)}>Önceki</button>
            <span className="text-sm text-slate-600">Sayfa {page}</span>
            <button className={chipBtnCls + " py-1"} disabled={(page*pageSize)>=total} onClick={()=> setPage(p=>p+1)}>Sonraki</button>
          </div>
        </div>
      </div>

      {/* Select modals */}
      <TeamSelectModal
        isOpen={teamModal}
        onClose={()=> setTeamModal(false)}
        onSelect={(t)=> { setTeamId(t.id); setTeamName(t.name); setTeamModal(false) }}
      />
      <CustomerSelectModal
        isOpen={customerModal}
        onClose={()=> setCustomerModal(false)}
        onSelect={(c)=> { setFilters(f=> ({ ...f, customer_id: c.id })); setCustomerModal(false) }}
        loader={loadCustomers}
      />
      <BankSelectModal
        isOpen={bankModal}
        onClose={()=> setBankModal(false)}
        onSelect={(b)=> { setFilters(f=> ({ ...f, bank_id: b.id })); setBankModal(false) }}
      />
    </div>
  )
}

export default AdminInvestments
